import React from 'react';
import PropTypes from 'prop-types';
import II18 from '../translate/II18';
import {
  DOC_TYPE_DOCUMENT,
  DOC_TYPE_FORM,
  DOC_TYPE_IMMEDIATE_CONFIRMATION,
  DOC_TYPE_INFORMATION,
  DOC_TYPE_PDF_FORM,
  findNotReadyDocumentInList,
  isDocumentHasProblems,
  isStepHasProblems,
  prepareScanUtils
} from '../helpers/utils';
import { gaeList } from './ga';
import { create_UUID } from '../../redux/utils';

export default function withDocumentsList(WrappedComponent) {
  // noinspection JSPotentiallyInvalidUsageOfThis
  class WithDocumentsListWrapper extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        sendInfoFiles: false,
        highlightDocument: '',
        showThankyouPopUp: props.showThankyouPopUp
      };
      this.componentId = create_UUID();
    }

    componentDidMount() {
      if (this.haveDocumentsToUpload()) {
        prepareScanUtils();
      }
      this.lastPage = this.$f7router.history[this.$f7router.history.length - 1];

      if (
        !this.props.templateMode &&
        !this.props.scanEnded &&
        this.onlyInfoDocuments() &&
        !this.findNotReadyDocument()
      ) {
        gaeList('SEND_REQUEST_ON_ONLY_INFO_DOCS_REQUEST');
        this.props.onSendRequest();
      }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      if (
        !prevProps.currentActionForDocId &&
        this.props.currentActionForDocId
      ) {
        this.setState({
          highlightDocument: ''
        });
      }
      if (!prevProps.scanEnded && this.props.scanEnded) {
        this.props.removeCurrentActionDocument();
        this.setState({
          showThankyouPopUp: true
        });
      }
    }

    onCloseThankYouPopup = () => {
      this.setState({
        showThankyouPopUp: false
      });
    };

    getScannedPages = docId =>
      this.props.scannedDocuments && this.props.scannedDocuments.get(docId);

    findNotReadyDocument(pageIndex) {
      return findNotReadyDocumentInList(
        pageIndex === undefined
          ? this.getRequestedDocuments(true)
          : this.getRequestedDocumentsForPage(pageIndex)
      );
    }

    getRequestedDocumentById = id => {
      return this.getRequestedDocuments(true).find(doc => doc.id === id);
    };

    getDocumentIndex = docId => {
      return this.getRequestedDocuments().findIndex(doc => doc.id === docId);
    };

    showAlertDocumentNotReady = errDoc => {
      this.$f7.dialog.alert(
        II18.get('dialog_required_document_' + errDoc.type, errDoc.name),
        II18.get('dialog_attention')
      );
    };

    onSendRequest = () => {
      gaeList('SEND_BUTTON_CLICK');
      const errDoc = this.findNotReadyDocument();
      if (errDoc) {
        gaeList('SHOW_ALERT', 'DIALOG_LOAD_ALL_REQUIRED');
        this.showAlertDocumentNotReady(errDoc);
      } else {
        this.props.onSendRequest();
      }
    };

    haveDocumentsToUpload = () =>
      this.getRequestedDocuments(true).some(
        doc => doc.type === DOC_TYPE_DOCUMENT
      );

    haveDocumentsToSend = () =>
      this.getRequestedDocuments(true).some(doc =>
        [DOC_TYPE_DOCUMENT, DOC_TYPE_FORM, DOC_TYPE_PDF_FORM].includes(doc.type)
      );

    onlyInfoDocuments = () =>
      this.props.requestedDocuments.length === 1 &&
      this.getRequestedDocuments(true).every(
        doc => doc.type === DOC_TYPE_INFORMATION
      );

    isRequestOnlyInfoOrConfirmDocuments = () =>
      !this.isManyPagesRequest() &&
      this.isOnlyInfoOrConfirmDocumentsListed(this.getRequestedDocuments());

    isOnlyInfoOrConfirmDocumentsListed = () =>
      !this.isManyPagesRequest() &&
      this.getRequestedDocuments().every(
        doc =>
          DOC_TYPE_INFORMATION === doc.type ||
          DOC_TYPE_IMMEDIATE_CONFIRMATION === doc.type
      );

    isRequestPageDone = pageIndex => {
      return !this.findNotReadyDocument(pageIndex);
    };

    isManyPagesRequest = () => Array.isArray(this.props.requestPages);

    getRequestedDocumentsForPage = pageIndex => {
      return this.props.requestPages[pageIndex].documents;
    };

    //TODO: parameter all is a bed idea
    getRequestedDocuments = (all = false) => {
      const { requestedDocuments } = this.props;

      if (!all && this.isManyPagesRequest()) {
        return this.getRequestedDocumentsForPage(
          this.props.currentRequestPageIndex
        );
      } else return requestedDocuments;
    };

    getRequestPagesCount = () => {
      return this.props.requestPages.length;
    };

    isRequestPageVisited = index => {
      const requestedDocumentsForPage = this.getRequestedDocumentsForPage(
        index
      );
      return requestedDocumentsForPage.some(doc =>
        this.props.visitedDocuments.has(doc.id)
      );
    };

    getRequestPagesExt = () => {
      let allPrevStepDone = true;

      return this.props.requestPages.map((page, index) => {
        const oneDocument = page.documents.length === 1;
        const stepIsDone = page.summaryStep
          ? this.props.scanEnded
          : this.isRequestPageDone(index);

        const stepIsVisited =
          this.isRequestPageVisited(index) ||
          this.props.scanEnded ||
          page.submitted;

        const result = {
          stepTitle: page.stepTitle,
          title:
            page.title ||
            (oneDocument
              ? page.documents[0].name
              : II18.get('title_step') + ' ' + (index + 1)),
          done: stepIsDone,
          summaryStep: page.summaryStep,
          submitted:
            (!page.summaryStep && page.submitted) || this.props.scanEnded,
          visited: stepIsVisited,
          readyToUse: allPrevStepDone,
          documents: page.documents,
          hasProblems: isStepHasProblems(
            this.props.errorSentKeys,
            page.documents,
            this.props.scannedDocuments
          ),
          docsWithProblems: page.documents.reduce((acc, currentValue) => {
            if (
              isDocumentHasProblems(
                this.props.errorSentKeys,
                currentValue.id,
                this.props.scannedDocuments.get(currentValue.id)
              )
            ) {
              acc.add(currentValue.id);
            }
            return acc;
          }, new Set())
        };
        allPrevStepDone = allPrevStepDone && stepIsDone;
        return result;
      });
    };

    goToNextRequestPage = () => {
      if (
        this.getCurrentActionDocument() &&
        this.getCurrentRequestPageInfo().documents.length > 1
      ) {
        this.props.removeCurrentActionDocument();
      } else {
        const errDoc = this.findNotReadyDocument(
          this.props.currentRequestPageIndex
        );
        if (errDoc) {
          this.showAlertDocumentNotReady(errDoc);
          this.setState({
            highlightDocument: errDoc.id
          });
        } else {
          this.props.goToRequestPage(this.props.currentRequestPageIndex + 1);
        }
      }
    };

    getCurrentRequestPageInfo = () =>
      this.getRequestPagesExt()[this.props.currentRequestPageIndex];

    /*

    completePage = n => {
      this.setState({
        waitPageCompleted: n
      });
      this.onSendRequest(this.props.requestPages[n].pageId);
    };

    goToRequestPage = n => {
      if (n > 0 && this.props.isRequestPageNeedToComplete(n - 1)) {
        confirmDialog(
          II18.get('complete_page_title'),
          II18.get('complete_page_message', this.props.companyName),
          () => this.completePage(n - 1),
          null,
          II18.get('complete_page_perform')
        );
      } else {
        const summaryStep = this.props.requestPages[n].summaryStep;
        if (
          this.getCurrentActionDocument() &&
          (this.getCurrentRequestPageInfo().documents.length > 1 ||
            n !== this.props.currentRequestPageIndex ||
            summaryStep)
        ) {
          this.props.removeCurrentActionDocument();
        }

        gaeList(
          'GO_TO_REQUEST_PAGE',
          `${n + 1}: ${this.props.requestPages[n].title}`
        );
        this.props.setCurrentRequestPageIndex(n);
      }
    };*/

    goToPrevRequestPage = () => {
      if (
        this.getCurrentActionDocument() &&
        this.getCurrentRequestPageInfo().documents.length > 1
      ) {
        this.props.removeCurrentActionDocument();
      } else {
        this.props.currentRequestPageIndex > 0
          ? this.props.goToRequestPage(this.props.currentRequestPageIndex - 1)
          : this.props.navigateBack(true);
      }
    };

    prepareManyPagesRequest = () => {
      if (this.isManyPagesRequest()) {
        const requestPagesExt = this.getRequestPagesExt();
        const activeRequestPageExt =
          requestPagesExt[this.props.currentRequestPageIndex];
        return {
          pagesInfo: requestPagesExt,
          oneDocumentStep: activeRequestPageExt.documents.length === 1,
          goToRequestPage: this.props.goToRequestPage,
          goToNextRequestPage: this.goToNextRequestPage,
          goToPrevRequestPage: this.goToPrevRequestPage,
          activePageDone: activeRequestPageExt.done,
          activePageTitle: activeRequestPageExt.title,
          actionDocument: activeRequestPageExt.documents.find(
            doc => doc.id === this.props.currentActionForDocId
          ),
          activePageIndex: this.props.currentRequestPageIndex,
          isManyPagesRequest: true,
          requestPagesCount: this.getRequestPagesCount(),
          summaryStep: activeRequestPageExt.summaryStep
        };
      } else
        return {
          isManyPagesRequest: false
        };
    };

    getCurrentActionDocument = () => {
      return (
        this.props.currentActionForDocId &&
        this.getRequestedDocumentById(this.props.currentActionForDocId)
      );
    };

    navigateToInPageDocument = doc => {
      this.props.navigateToInPageDocument(doc);
    };

    onRejectDocument = docId => {
      this.props.showRejectDocumentDialog(docId);
    };

    render() {
      const manyPagesRequestInfo = this.prepareManyPagesRequest();
      const actionDocument =
        manyPagesRequestInfo.actionDocument || this.getCurrentActionDocument();
      return (
        <>
          <WrappedComponent
            componentId={this.componentId}
            onRejectDocument={this.onRejectDocument}
            navigateToInPageDocument={this.navigateToInPageDocument}
            onCloseThankYouPopup={this.onCloseThankYouPopup}
            highlightDocument={this.state.highlightDocument}
            onlyInfoDocuments={this.onlyInfoDocuments()}
            haveDocumentsToSend={this.haveDocumentsToSend()}
            haveDocumentsToUpload={this.haveDocumentsToUpload()}
            isDocumentsReadyToSend={!this.findNotReadyDocument()}
            getScannedPages={this.getScannedPages}
            getRequestedDocumentById={this.getRequestedDocumentById}
            {...manyPagesRequestInfo}
            actionDocument={actionDocument}
            actionDocumentIndex={
              actionDocument && this.getDocumentIndex(actionDocument.id)
            }
            {...this.props}
            onSendRequest={() => this.onSendRequest()}
            showThankyouPopUp={this.state.showThankyouPopUp}
            requestedDocuments={this.getRequestedDocuments()}
          />
        </>
      );
    }
  }

  WithDocumentsListWrapper.propTypes = {
    selectedLang: PropTypes.string,
    visitedDocuments: PropTypes.object,
    brandingDetails: PropTypes.object,
    cameraMode: PropTypes.bool,
    caseName: PropTypes.string,
    companyEmail: PropTypes.string,
    companyName: PropTypes.string,
    documentsStatus: PropTypes.object,
    errorMessage: PropTypes.string,
    expired: PropTypes.bool,
    forms: PropTypes.object,
    infoMessage: PropTypes.string,
    privacyAccepted: PropTypes.bool,
    requestedDocuments: PropTypes.array,
    scanEnded: PropTypes.bool,
    scannedDocuments: PropTypes.object,
    setCameraMode: PropTypes.func,
    setPrivacyAccepted: PropTypes.func,
    showThankyouPopUp: PropTypes.bool,
    submittedDate: PropTypes.string,
    successSentKeys: PropTypes.object,
    errorSentKeys: PropTypes.object,
    templateMode: PropTypes.bool,
    requestPages: PropTypes.any,
    setCurrentRequestPageIndex: PropTypes.func,
    currentRequestPageIndex: PropTypes.number,
    navigateToInPageDocument: PropTypes.func,
    usersDataOk: PropTypes.bool,
    navigateBack: PropTypes.func,
    showRejectDocumentDialog: PropTypes.func,

    //withDocumentRootLogic
    currentActionForDocId: PropTypes.string,
    removeCurrentActionDocument: PropTypes.func,
    goToRequestPage: PropTypes.func,
    onSendRequest: PropTypes.func
  };

  return WithDocumentsListWrapper;
}
